import GqlEvents from "@insightfulscience/shared-utils/gqlEvents";
import { PURCHASE_TYPE } from "../../domain/purchase";
import { isPersonalPlan } from "../../domain/plan";

const gql = GqlEvents();

const purchaseGtmEvent = new Map([
	[PURCHASE_TYPE.SUBSCRIPTION, "Purchased Subscription"],
	[PURCHASE_TYPE.RENEW, "Renewed Subscription"],
	[PURCHASE_TYPE.ADDON, "Purchased Subscription Seats"],
]);

const isPersonalSubscriptionPurchase = (plan, purchaseType) =>
	isPersonalPlan(plan) && purchaseType === PURCHASE_TYPE.SUBSCRIPTION;

const sendRegularPurchaseEvent = (orderNumber, order, purchaseType, seats, price) => {
	const event = purchaseGtmEvent.get(purchaseType) ?? "unknown purchase event";
	global.dataLayer = global.dataLayer || [];
	global.dataLayer.push({
		event,
		data: {
			planCode: order.subscription.plan.planCode,
			quantity: isPersonalSubscriptionPurchase(order.subscription.plan, purchaseType) ? 1 : seats,
			revenue: Math.round(price * 100),
			subscriptionId: order.subscriptionID,
			orderNumber,
		},
	});
};

const sendUpgradeTrialPurchaseEvent = (orderNumber, order, seats, price) => {
	global.dataLayer = global.dataLayer || [];
	global.dataLayer.push({
		event: "Purchased Trial",
		data: {
			planCode: order.subscription.plan.planCode,
			quantity: seats + 1,
			revenue: Math.round(price * 100),
			subscriptionId: order.subscriptionID,
			isTrialUpgrade: 1,
			orderNumber,
		},
	});
};

gql.on(
	"purchaseV2",
	(
		_,
		{ purchaseV2: { orderNumber, order, errorCode } },
		{ purchaseType, seats, newSeats, price },
	) => {
		if (errorCode) return;

		if (order.subscription?.isFromTrial) {
			sendUpgradeTrialPurchaseEvent(orderNumber, order, newSeats, price);
		} else {
			sendRegularPurchaseEvent(orderNumber, order, purchaseType, seats, price);
		}
	},
);

gql.on(
	"cancelSubscriptionByIDV2",
	(
		{ subscriptionID: subscriptionId },
		{ cancelSubscriptionByIDV2: { errorCode } },
		{ planCode },
	) => {
		if (errorCode) return;

		global.dataLayer.push({
			event: "Canceled Subscription",
			data: { planCode, subscriptionId },
		});
	},
);

gql.on(
	"reactivateSubscriptionV2",
	({ subscriptionId }, { reactivateSubscriptionByIDV2: { errorCode } }, { planCode }) => {
		if (errorCode) return;

		global.dataLayer.push({
			event: "Reactivated Subscription",
			data: { planCode, subscriptionId },
		});
	},
);

gql.on(
	"updatePaymentMethodV2",
	({ subscriptionID }, { updatePaymentMethodV2: { errorCode } }, { paymentType }) => {
		if (errorCode) return;

		global.dataLayer.push({
			event: "Changed Subscription Payment Method",
			data: {
				paymentMethod: paymentType,
				subscriptionId: subscriptionID,
			},
		});
	},
);

gql.on(
	"updateSubscriptionByIDV2",
	(
		{ subscriptionId, newSeats },
		{ updateSubscriptionByIDV2: { orderNumber, totalCharged, errorCode } },
		{ planCode },
	) => {
		if (errorCode) return;

		global.dataLayer.push({
			event: "Added Subscription Seats",
			data: {
				quantity: newSeats,
				revenue: Math.round(totalCharged * 100),
				subscriptionId,
				orderNumber,
				planCode,
			},
		});
	},
);

gql.on(
	"changeSubscriptionPlanV2",
	(
		{ subscriptionID, newPlanCode, newSubscriptionSize },
		{ changeSubscriptionPlanV2: { totalCharged, orderNumber, errorCode } },
		{ planCode },
	) => {
		if (errorCode) return;

		global.dataLayer.push({
			event: "Changed Subscription Plan",
			data: {
				subscriptionId: subscriptionID,
				currentPlanCode: planCode,
				planCode: newPlanCode,
				quantity: newSubscriptionSize,
				revenue: Math.round(totalCharged * 100),
				orderNumber,
			},
		});
	},
);

gql.on("requestTrial", ({ email }) => {
	global.dataLayer.push({
		event: "Requested Trial",
		data: {
			user: {
				email,
			},
		},
	});
});

gql.on("startTrial", (_, { startTrial: { errorCode, trialStatus } }) => {
	if (errorCode || trialStatus !== "new") return;

	global.dataLayer.push({
		event: "Started Trial",
	});
});

const gqlEventsListener = () => gql.listener;

export default gqlEventsListener;
